import * as React from 'react';

import { ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import type { TemplateContent, TemplateFormFieldDef, TemplateFormFieldOption } from '@klareospace/pharos-lib-types';
import {
  Badge,
  Button,
  CredentialInput,
  DrawerFormSkeleton,
  DynamicFormField,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSectionHeader,
  Separator,
  SideDrawer,
} from '@klareospace/pharos-lib-web-utils';
import { useTranslation } from 'react-i18next';

import { ShipperAvatar } from './shipper-avatar';
import { useInstallShipperDrawer } from '../hooks/use-install-shipper-drawer';
import { isSecretField } from '../schemas/install-shipper.schema';

// ─── Types ────────────────────────────────────────────────────────────────────

export type InstallShipperDrawerMode = 'edit' | 'install';

export interface InstallShipperDrawerProps {
  /** The config id being edited — non-null puts the drawer in edit mode. */
  configId?: string | null;
  /** Optional existing configuration values for prefill. */
  existingValues?: Record<string, unknown>;
  /** 'install' (default) or 'edit'. */
  mode?: InstallShipperDrawerMode;
  onClose: () => void;
  open: boolean;
  /** ID of the template whose fields to render. */
  templateId: string | null;
}

// ─── Section partitioning ─────────────────────────────────────────────────────

/**
 * Extracts all `{{values.KEY}}` placeholder keys from a string.
 */
function extractValueKeys(template: string): string[] {
  const matches = [...template.matchAll(/\{\{values\.([^}]+)\}\}/g)];
  return matches.map((m) => m[1]);
}

/**
 * Adds all `{{values.KEY}}` placeholder keys found in an auth configuration
 * object into the accumulator set.
 */
function addAuthConfigKeys(config: Record<string, string> | undefined, keys: Set<string>): void {
  if (!config) return;
  for (const val of Object.values(config)) {
    for (const key of extractValueKeys(val as string)) {
      keys.add(key);
    }
  }
}

/**
 * Collects all form-field keys that appear in auth configuration templates
 * within the incoming section.
 */
function collectIncomingKeys(content: TemplateContent): Set<string> {
  const keys = new Set<string>();
  const incoming = content.incoming;
  if (!incoming) {
    return keys;
  }
  addAuthConfigKeys(incoming.authentication?.configuration, keys);
  for (const ep of incoming.endpointConfigs ?? []) {
    addAuthConfigKeys(ep.authentication?.configuration, keys);
  }
  return keys;
}

/**
 * Collects all form-field keys that appear in auth configuration or URL
 * templates within the outgoing section.
 */
function collectOutgoingKeys(content: TemplateContent): Set<string> {
  const keys = new Set<string>();
  const outgoing = content.outgoing;
  if (!outgoing) {
    return keys;
  }
  addAuthConfigKeys(outgoing.authentication?.configuration, keys);
  for (const wh of outgoing.webhookConfigs ?? []) {
    for (const key of extractValueKeys(wh.url)) {
      keys.add(key);
    }
    addAuthConfigKeys(wh.authentication?.configuration, keys);
  }
  return keys;
}

interface PartitionedFields {
  incoming: TemplateFormFieldDef[];
  outgoing: TemplateFormFieldDef[];
  ungrouped: TemplateFormFieldDef[];
}

/**
 * Partitions form fields into Incoming / Outgoing / Ungrouped buckets based
 * on their references in the template's auth and URL descriptors.
 * Fields not referenced in either section fall into `ungrouped`.
 */
function partitionFormFields(
  formFields: TemplateFormFieldDef[],
  content: TemplateContent | undefined,
): PartitionedFields {
  if (!content) {
    return { incoming: [], outgoing: [], ungrouped: formFields };
  }

  const incomingKeys = collectIncomingKeys(content);
  const outgoingKeys = collectOutgoingKeys(content);

  const incoming: TemplateFormFieldDef[] = [];
  const outgoing: TemplateFormFieldDef[] = [];
  const ungrouped: TemplateFormFieldDef[] = [];

  for (const field of formFields) {
    if (incomingKeys.has(field.key)) {
      incoming.push(field);
    } else if (outgoingKeys.has(field.key)) {
      outgoing.push(field);
    } else {
      ungrouped.push(field);
    }
  }

  return { incoming, outgoing, ungrouped };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

interface SubmitDisabledParams {
  activeMutationPending: boolean;
  isDirty: boolean;
  isEditMode: boolean;
  isLoading: boolean;
  isValid: boolean;
}

function isSubmitButtonDisabled({
  activeMutationPending,
  isDirty,
  isEditMode,
  isLoading,
  isValid,
}: SubmitDisabledParams): boolean {
  const baseDisabled = !isValid || activeMutationPending || isLoading;
  return isEditMode ? !isDirty || baseDisabled : baseDisabled;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface EditSecretFieldProps {
  control: ReturnType<typeof useInstallShipperDrawer>['form']['control'];
  fieldDef: TemplateFormFieldDef;
  helpText: string;
  hideLabel: string;
  placeholder: string;
  showLabel: string;
}

/**
 * Renders a CredentialInput (key icon + eye-reveal) for password/jwt fields in
 * edit mode with leave-blank-to-keep helper text and a bullet placeholder.
 */
function EditSecretField({
  control,
  fieldDef,
  helpText,
  hideLabel,
  placeholder,
  showLabel,
}: EditSecretFieldProps): React.JSX.Element {
  return (
    <FormField
      control={control}
      name={fieldDef.key}
      render={({ field }): React.JSX.Element => (
        <FormItem>
          <FormLabel>
            {fieldDef.label}
            {fieldDef.required ? <span className="text-destructive"> *</span> : null}
          </FormLabel>
          <FormControl>
            <CredentialInput
              {...field}
              hideLabel={hideLabel}
              placeholder={placeholder}
              showLabel={showLabel}
              value={(field.value as string) ?? ''}
            />
          </FormControl>
          <FormDescription>{helpText}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

interface FieldRendererProps {
  control: ReturnType<typeof useInstallShipperDrawer>['form']['control'];
  dynamicOptions: Record<string, TemplateFormFieldOption[]>;
  fieldDef: TemplateFormFieldDef;
  hideLabel: string;
  isEditMode: boolean;
  secretHelpText: string;
  secretPlaceholder: string;
  selectEmptyLabel: string;
  selectLoadingLabel: string;
  showLabel: string;
}

function FieldRenderer({
  control,
  dynamicOptions,
  fieldDef,
  hideLabel,
  isEditMode,
  secretHelpText,
  secretPlaceholder,
  selectEmptyLabel,
  selectLoadingLabel,
  showLabel,
}: FieldRendererProps): React.JSX.Element {
  if (isEditMode && isSecretField(fieldDef.type)) {
    return (
      <EditSecretField
        control={control}
        fieldDef={fieldDef}
        helpText={secretHelpText}
        hideLabel={hideLabel}
        key={fieldDef.key}
        placeholder={secretPlaceholder}
        showLabel={showLabel}
      />
    );
  }
  return (
    <DynamicFormField
      control={control}
      dynamicOptions={dynamicOptions}
      fieldDef={fieldDef}
      key={fieldDef.key}
      revealLabels={{ hide: hideLabel, show: showLabel }}
      selectEmptyLabel={selectEmptyLabel}
      selectLoadingLabel={selectLoadingLabel}
    />
  );
}

interface FormSectionProps {
  control: ReturnType<typeof useInstallShipperDrawer>['form']['control'];
  dynamicOptions: Record<string, TemplateFormFieldOption[]>;
  fields: TemplateFormFieldDef[];
  helperText: string;
  hideLabel: string;
  icon: React.ComponentProps<typeof FormSectionHeader>['icon'];
  isEditMode: boolean;
  secretHelpText: string;
  secretPlaceholder: string;
  selectEmptyLabel: string;
  selectLoadingLabel: string;
  showLabel: string;
  suffix?: string;
  title: string;
}

function FormSection({
  control,
  dynamicOptions,
  fields,
  helperText,
  hideLabel,
  icon,
  isEditMode,
  secretHelpText,
  secretPlaceholder,
  selectEmptyLabel,
  selectLoadingLabel,
  showLabel,
  suffix,
  title,
}: FormSectionProps): React.JSX.Element | null {
  if (fields.length === 0) {
    return null;
  }
  return (
    <div className="space-y-4">
      <FormSectionHeader icon={icon} suffix={suffix} title={title} />
      <p className="text-xs text-muted-foreground">{helperText}</p>
      {fields.map((fieldDef) => (
        <FieldRenderer
          control={control}
          dynamicOptions={dynamicOptions}
          fieldDef={fieldDef}
          hideLabel={hideLabel}
          isEditMode={isEditMode}
          key={fieldDef.key}
          secretHelpText={secretHelpText}
          secretPlaceholder={secretPlaceholder}
          selectEmptyLabel={selectEmptyLabel}
          selectLoadingLabel={selectLoadingLabel}
          showLabel={showLabel}
        />
      ))}
    </div>
  );
}

interface ShipperFormBodyProps {
  control: ReturnType<typeof useInstallShipperDrawer>['form']['control'];
  dynamicOptions: Record<string, TemplateFormFieldOption[]>;
  emptyBodyMessage: string;
  formFields: TemplateFormFieldDef[];
  hideLabel: string;
  incomingHelperText: string;
  incomingDirectionSuffix: string;
  incomingSectionTitle: string;
  isEditMode: boolean;
  outgoingHelperText: string;
  outgoingDirectionSuffix: string;
  outgoingSectionTitle: string;
  secretHelpText: string;
  secretPlaceholder: string;
  selectEmptyLabel: string;
  selectLoadingLabel: string;
  showLabel: string;
  templateContent: TemplateContent | undefined;
}

function ShipperFormBody({
  control,
  dynamicOptions,
  emptyBodyMessage,
  formFields,
  hideLabel,
  incomingDirectionSuffix,
  incomingHelperText,
  incomingSectionTitle,
  isEditMode,
  outgoingDirectionSuffix,
  outgoingHelperText,
  outgoingSectionTitle,
  secretHelpText,
  secretPlaceholder,
  selectEmptyLabel,
  selectLoadingLabel,
  showLabel,
  templateContent,
}: ShipperFormBodyProps): React.JSX.Element {
  const { incoming, outgoing, ungrouped } = partitionFormFields(formFields, templateContent);
  const hasSections = incoming.length > 0 || outgoing.length > 0;

  if (formFields.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-muted-foreground">{emptyBodyMessage}</p>
      </div>
    );
  }

  if (!hasSections) {
    return (
      <form className="space-y-4 px-1 py-4">
        {formFields.map((fieldDef) => (
          <FieldRenderer
            control={control}
            dynamicOptions={dynamicOptions}
            fieldDef={fieldDef}
            hideLabel={hideLabel}
            isEditMode={isEditMode}
            key={fieldDef.key}
            secretHelpText={secretHelpText}
            secretPlaceholder={secretPlaceholder}
            selectEmptyLabel={selectEmptyLabel}
            selectLoadingLabel={selectLoadingLabel}
            showLabel={showLabel}
          />
        ))}
      </form>
    );
  }

  return (
    <form className="space-y-6 px-1 py-4">
      {incoming.length > 0 ? (
        <>
          <FormSection
            control={control}
            dynamicOptions={dynamicOptions}
            fields={incoming}
            helperText={incomingHelperText}
            hideLabel={hideLabel}
            icon={ArrowDownToLine}
            isEditMode={isEditMode}
            secretHelpText={secretHelpText}
            secretPlaceholder={secretPlaceholder}
            selectEmptyLabel={selectEmptyLabel}
            selectLoadingLabel={selectLoadingLabel}
            showLabel={showLabel}
            suffix={incomingDirectionSuffix}
            title={incomingSectionTitle}
          />
          {outgoing.length > 0 ? <Separator /> : null}
        </>
      ) : null}
      {outgoing.length > 0 ? (
        <FormSection
          control={control}
          dynamicOptions={dynamicOptions}
          fields={outgoing}
          helperText={outgoingHelperText}
          hideLabel={hideLabel}
          icon={ArrowUpFromLine}
          isEditMode={isEditMode}
          secretHelpText={secretHelpText}
          secretPlaceholder={secretPlaceholder}
          selectEmptyLabel={selectEmptyLabel}
          selectLoadingLabel={selectLoadingLabel}
          showLabel={showLabel}
          suffix={outgoingDirectionSuffix}
          title={outgoingSectionTitle}
        />
      ) : null}
      {ungrouped.length > 0 ? (
        <div className="space-y-4">
          {ungrouped.map((fieldDef) => (
            <FieldRenderer
              control={control}
              dynamicOptions={dynamicOptions}
              fieldDef={fieldDef}
              hideLabel={hideLabel}
              isEditMode={isEditMode}
              key={fieldDef.key}
              secretHelpText={secretHelpText}
              secretPlaceholder={secretPlaceholder}
              selectEmptyLabel={selectEmptyLabel}
              selectLoadingLabel={selectLoadingLabel}
              showLabel={showLabel}
            />
          ))}
        </div>
      ) : null}
    </form>
  );
}

interface ShipperDrawerFooterProps {
  cancelLabel: string;
  isSubmitDisabled: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
}

function ShipperDrawerFooter({
  cancelLabel,
  isSubmitDisabled,
  onCancel,
  onSubmit,
  submitLabel,
}: ShipperDrawerFooterProps): React.JSX.Element {
  return (
    <>
      <Button disabled={isSubmitDisabled} onClick={onSubmit} type="button">
        {submitLabel}
      </Button>
      <Button onClick={onCancel} type="button" variant="outline">
        {cancelLabel}
      </Button>
    </>
  );
}

// ─── Drawer header identity block (PHA-287 polish) ────────────────────────────

interface DrawerIdentityBlockProps {
  code: string | undefined;
  connectorTypeLabel: string;
  logoUrl: string | undefined;
  name: string;
}

function DrawerIdentityBlock({ code, connectorTypeLabel, logoUrl, name }: DrawerIdentityBlockProps): React.JSX.Element {
  return (
    <>
      <div className="flex items-center gap-3 pb-4">
        <ShipperAvatar logoUrl={logoUrl} name={name} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-semibold leading-tight">{name}</p>
            {code ? <span className="text-xs text-muted-foreground">{code}</span> : null}
          </div>
          <Badge className="mt-1" variant="secondary">
            {connectorTypeLabel}
          </Badge>
        </div>
      </div>
      <Separator />
    </>
  );
}

// ─── Drawer label helpers ─────────────────────────────────────────────────────

interface DrawerLabels {
  cancelLabel: string;
  subtitle: string;
  submitLabel: string;
  title: string;
}

function resolveDrawerLabels(
  t: (key: string) => string,
  editNs: string,
  isEditMode: boolean,
  activeMutationPending: boolean,
  templateName: string | undefined,
): DrawerLabels {
  const pendingKey = isEditMode ? 'saving' : 'installing';
  return {
    cancelLabel: t(`shippers.${editNs}.drawer.cancel`),
    submitLabel: activeMutationPending
      ? t(`shippers.${editNs}.drawer.${pendingKey}`)
      : t(`shippers.${editNs}.drawer.submit`),
    subtitle: t(`shippers.${editNs}.drawer.subtitle`),
    title: templateName ?? t(`shippers.${editNs}.drawer.title`),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InstallShipperDrawer({
  configId = null,
  existingValues,
  mode = 'install',
  onClose,
  open,
  templateId,
}: InstallShipperDrawerProps): React.JSX.Element {
  const { t } = useTranslation('settings');
  const isEditMode = mode === 'edit';

  const {
    activeMutationPending,
    dynamicOptions,
    form,
    formFields,
    isLoading,
    onSubmit,
    templateCode,
    templateContent,
    templateLogo,
    templateName,
  } = useInstallShipperDrawer({
    configId,
    existingValues,
    mode,
    onClose,
    open,
    templateId,
  });

  function handleClose(): void {
    form.reset();
    onClose();
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) handleClose();
  }

  const { isDirty, isValid } = form.formState;
  const editNs = isEditMode ? 'edit' : 'install';
  const { cancelLabel, submitLabel, subtitle, title } = resolveDrawerLabels(
    t,
    editNs,
    isEditMode,
    activeMutationPending,
    templateName,
  );

  const isSubmitDisabled = isSubmitButtonDisabled({
    activeMutationPending,
    isDirty,
    isEditMode,
    isLoading,
    isValid,
  });

  return (
    <SideDrawer
      dismissOnOutsideClick={false}
      footer={
        <ShipperDrawerFooter
          cancelLabel={cancelLabel}
          isSubmitDisabled={isSubmitDisabled}
          onCancel={handleClose}
          onSubmit={form.handleSubmit(onSubmit)}
          submitLabel={submitLabel}
        />
      }
      onOpenChange={handleOpenChange}
      open={open}
      subtitle={subtitle}
      title={title}
    >
      {isLoading ? (
        <DrawerFormSkeleton />
      ) : (
        <div className="space-y-4">
          {templateName ? (
            <DrawerIdentityBlock
              code={templateCode}
              connectorTypeLabel={t('shippers.drawer.connectorType')}
              logoUrl={templateLogo}
              name={templateName}
            />
          ) : null}
          <Form {...form}>
            <ShipperFormBody
              control={form.control}
              dynamicOptions={dynamicOptions}
              emptyBodyMessage={t('shippers.drawer.emptyBody')}
              formFields={formFields}
              hideLabel={t('common.reveal.hide')}
              incomingDirectionSuffix={t('shippers.drawer.incoming.direction')}
              incomingHelperText={t('shippers.drawer.incoming.helperText')}
              incomingSectionTitle={t('shippers.drawer.incoming.title')}
              isEditMode={isEditMode}
              outgoingDirectionSuffix={t('shippers.drawer.outgoing.direction')}
              outgoingHelperText={t('shippers.drawer.outgoing.helperText')}
              outgoingSectionTitle={t('shippers.drawer.outgoing.title')}
              secretHelpText={t('shippers.edit.drawer.passwordHelp')}
              secretPlaceholder={t('shippers.edit.drawer.passwordPlaceholder')}
              selectEmptyLabel={t('common.dynamicSelect.empty')}
              selectLoadingLabel={t('common.dynamicSelect.loading')}
              showLabel={t('common.reveal.show')}
              templateContent={templateContent}
            />
          </Form>
        </div>
      )}
    </SideDrawer>
  );
}
